import { useEffect, useMemo, useState } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import { products as productsRaw } from './data/products';

import Product, { ProductSelect } from './components/product';

import './App.css';
// import Tabs from './components/tabs';
import ShoppingList from './components/shopping-list';
import DarkLightModeSwitch from './components/dark-light-mode-switch';
import { storage as confStorage } from './config';
import { CATALYST, SPEC_MAIN } from './enum';
import { catalysts } from './data/catalysts';
import { bases } from './data/bases';
import SpoilerAlert from './components/spoiler-alert';
import { items } from './data/items';

const { version } = require('../package.json');

// Sort products A-Z.
const products = productsRaw.sort((a, b) => a.name.localeCompare(b.name));
const currYear = new Date().getFullYear();

function App() {
	const [theme, setTheme] = useState(localStorage.getItem(confStorage.theme) || 'dark');
	const [basket, setBasket] = useState([null]);
	const [ingredients, setIngredients] = useState([[null, null, null, null]]);
	const [activeItem, setActiveItem] = useState(0);

	const itemsToShop = useMemo(() => {
		const items = [...ingredients.flat()].map((i) => {
			return i;
		});
		// TODO: Currently there are no specific main items.
		// basket.forEach((b) => {
		// 	if (b?.specificMain !== SPEC_MAIN.NONE) {
		// 		items.unshift(b.specificMain);
		// 	}
		// });
		basket.forEach((b) => {
			if (b && b?.catalyst !== CATALYST.NONE) {
				items.unshift(catalysts[b.catalyst]);
			}
		});
		basket.forEach((b) => {
			if (b && b?.base) {
				// Base is needed twice.
				items.unshift({ ...bases[b.base], productId: b.id });
				items.unshift({ ...bases[b.base], productId: b.id });
			}
		});
		return items;
	}, [basket, ingredients]);

	const onSelectProduct = (e, index) => {
		const oldId = basket[index]?.id;
		const pId = parseInt(e.target.value);
		const p = products.find((p) => p.id === pId);

		if (p.specificMain !== SPEC_MAIN.NONE) {
			const specMain = products.find((_p) => _p.id === p.specificMain);
			p.specificMain = { ...items[specMain.element][specMain.type].find((_p) => _p.id === p.specificMain), isSpecificMain: true };
		}
		const newBasket = [...basket];
		newBasket[index] = p;
		setBasket(newBasket);
		if (oldId !== pId) {
			const newIngredients = [...ingredients];
			newIngredients[index] = [null, null, null, null];
			if (p?.specificMain?.id) {
				newIngredients[index][0] = { ...p.specificMain, productId: p.id, isMain: true };
			}
			setIngredients(newIngredients);
		}
	};

	const onSelectCatalyst = (catalystId, productIndex) => {
		const p = products.find((p) => p.id === catalystId);
		const newBasket = [...basket];
		const newIngredients = [...ingredients];
		// Add the catalyst before the product.
		newBasket.splice(productIndex, 0, p);
		setBasket(newBasket);
		newIngredients.splice(productIndex, 0, [null, null, null, null]);
		setIngredients(newIngredients);
		setActiveItem(productIndex + 1);
	};

	const onSelectSpecificMain = (specificMainId, productIndex) => {
		const p = products.find((p) => p.id === specificMainId);
		const newBasket = [...basket];
		const newIngredients = [...ingredients];
		// Add the specific main before the product.
		newBasket.splice(productIndex, 0, p);
		setBasket(newBasket);
		newIngredients.splice(productIndex, 0, [null, null, null, null]);
		setIngredients(newIngredients);
		setActiveItem(productIndex + 1);
	};

	const onSelectAgent = (agentId, productIndex) => {
		const p = products.find((p) => p.id === agentId);
		const newBasket = [...basket];
		const newIngredients = [...ingredients];
		// Add the agent after the product.
		newBasket.splice(productIndex + 1, 0, p);
		setBasket(newBasket);
		newIngredients.splice(productIndex + 1, 0, [null, null, null, null]);
		setIngredients(newIngredients);
		setActiveItem(productIndex + 1);
	};

	useEffect(() => {
		localStorage.setItem(confStorage.theme, theme);
	}, [theme]);

	return (
		<div className={`App is-mode-${theme}`}>
			<SpoilerAlert />
			<ShoppingList basket={basket} items={itemsToShop} ingredients={ingredients} />
			<DarkLightModeSwitch theme={theme} setTheme={setTheme} />
			<Tabs
				className="tabs"
				selectedIndex={activeItem}
				onSelect={(i, l, e) => {
					if (e.target?.role === 'tab') {
						setActiveItem(i);
					} else {
						const newBasket = [...basket];
						const newIngredients = [...ingredients];
						newBasket.splice(i, 1);
						newIngredients.splice(i, 1);
						setBasket(newBasket);
						setIngredients(newIngredients);
						setActiveItem(0);
					}
				}}
			>
				<TabList>
					{basket.map((t, ti) => (
						<Tab key={ti}>
							{t?.name || '...'}
							{basket.length > 1 && (
								<div className={'react-tabs__tab--close'}>
									<img
										src={'./assets/icon--close.svg'}
										alt="Close Tab"
										title="Close Tab"
									/>
								</div>
							)}
						</Tab>
					))}
					{basket[basket.length - 1]?.name && (
						<Tab onClick={() => {
							setBasket([...basket, null]);
							setIngredients([...ingredients, [null, null, null, null]]);
							setActiveItem(basket.length);
						}}>
							Add
						</Tab>
					)}
				</TabList>
				{basket.map((t, ti) => (
					<TabPanel key={ti}>
						<div className="basket__item">
							<div className="basket__item--input-wrapper">
								<ProductSelect
									value={basket[ti]?.id || null}
									onChange={(e) => onSelectProduct(e, ti)}
								/>
							</div>
							{basket[ti] && (
								<Product
									product={basket[ti]}
									ingredients={ingredients[ti] || [null, null, null, null]}
									setIngredients={(val) => {
										const newIngredients = [...ingredients];
										newIngredients[ti] = val;
										setIngredients(newIngredients);
									}}
									onAddCatalystToBasket={(catalystId) => onSelectCatalyst(catalystId, ti)}
									onAddSpecificMainToBasker={(specificMainId) => onSelectSpecificMain(specificMainId, ti)}
									onAddAgentToBasket={(agentId) => onSelectAgent(agentId, ti)}
								/>
							)}
						</div>
					</TabPanel>
				))}
				{basket[basket.length - 1]?.name && (<TabPanel></TabPanel>)}
			</Tabs>
			<footer style={{ marginTop: '1.5rem' }}>
				<p>
					<small>
						Arcadia Quest Helper v{version} &copy; {currYear} by Sacer{' '}|{' '}Want to report a bug or suggest an improvement? Find me at the Arcadia Discord.{' '}|{' '}
						<a href="https://wiki.arcadia-online.org/Atelier_Richelle/" target="_blank" rel="noreferrer">Atelier Quest Guide</a>
					</small>
				</p>
			</footer>
		</div>
	);
};

export default App;
